import styled from "@emotion/styled";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import Popover from "components/ui/popover/Popover";
import { SelectContent, SelectItem, SelectValue } from "components/ui/select/Select";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { NoticeSemanticColumnField } from "lib/types/savedViews";
import {
  getAllowedSortFieldsForColumns,
  getDefaultSortOrderForField,
  getSemanticSortFieldLabel,
  getSemanticSortOrderLabels,
  type NoticeSemanticSort,
} from "./noticeSemanticSort";

type Props = {
  sort: NoticeSemanticSort;
  /** Same column list as the table (URL / saved view / defaults today; column picker when added). */
  columnFields: NoticeSemanticColumnField[];
  onSortChange: (next: NoticeSemanticSort) => void;
  disabled?: boolean;
};

export function NoticeSemanticSortByCard({
  sort,
  columnFields,
  onSortChange,
  disabled = false,
}: Props) {
  const fieldOptions = getAllowedSortFieldsForColumns(columnFields);
  const orderLabels = getSemanticSortOrderLabels(sort.field);
  const fieldLabel = getSemanticSortFieldLabel(sort.field);
  const sortIcon = sort.order === "DESC" ? "sortDescending" : "sortAscending";

  return (
    <Popover
      hideArrow
      side="bottom"
      align="start"
      contentPadding={12}
      borderRadius={8}
      trigger={
        <Button
          buttonType="secondary"
          icon={sortIcon}
          disabled={disabled}
          aria-label="Open sort menu"
        >
          {fieldLabel}
        </Button>
      }
    >
      <SortCard>
        <TitleText>Sort by</TitleText>
        <SelectStack>
          <SortSelectRoot
            value={sort.field}
            onValueChange={(field) => {
              const nextField = field as NoticeSemanticColumnField;
              onSortChange({
                field: nextField,
                order: getDefaultSortOrderForField(nextField),
              });
            }}
            disabled={disabled}
          >
            <SortSelectTrigger aria-label="Sort by field">
              <TriggerLabel>
                <SelectValue />
              </TriggerLabel>
              <UIcon icon="chevronDown" size={12} />
            </SortSelectTrigger>
            <SelectContent position="popper" sideOffset={4}>
              {fieldOptions.map((field) => (
                <SelectItem key={field} value={field}>
                  {getSemanticSortFieldLabel(field)}
                </SelectItem>
              ))}
            </SelectContent>
          </SortSelectRoot>

          <SortSelectRoot
            value={sort.order}
            onValueChange={(order) =>
              onSortChange({
                ...sort,
                order: order as NoticeSemanticSort["order"],
              })
            }
            disabled={disabled}
          >
            <SortSelectTrigger aria-label="Sort direction">
              <TriggerLabel>
                <SelectValue />
              </TriggerLabel>
              <UIcon icon="chevronDown" size={12} />
            </SortSelectTrigger>
            <SelectContent position="popper" sideOffset={4}>
              <SelectItem value="DESC">{orderLabels.desc}</SelectItem>
              <SelectItem value="ASC">{orderLabels.asc}</SelectItem>
            </SelectContent>
          </SortSelectRoot>
        </SelectStack>
      </SortCard>
    </Popover>
  );
}

const SortSelectRoot = styled(SelectPrimitive.Root)({
  width: "100%",
});

const SortSelectTrigger = styled(SelectPrimitive.Trigger)(({ theme }) => ({
  all: "unset",
  boxSizing: "border-box",
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  padding: "5px 12px",
  minHeight: 32,
  borderRadius: 4,
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
  backgroundColor: theme.colors.sysBackgroundDefault,
  cursor: "pointer",
  fontSize: 14,
  fontWeight: theme.font.fontWeightMedium,
  lineHeight: "22px",
  color: theme.colors.sysTextBlack,

  "&:disabled": {
    cursor: "not-allowed",
    opacity: 0.5,
  },

  "&:focus-visible": {
    outline: `2px solid ${theme.colors.sysPrimaryDefault}`,
    outlineOffset: 1,
  },
}));

const TriggerLabel = styled.span({
  flex: "1 1 0",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  textAlign: "left",
});

const SortCard = styled(Flex)({
  flexDirection: "column",
  gap: 8,
  width: 208,
  flexShrink: 0,
});

const TitleText = styled(Text)(({ theme }) => ({
  fontSize: 14,
  fontWeight: theme.font.fontWeightMedium,
  lineHeight: "22px",
  color: theme.colors.sysTextBlack,
  width: "100%",
}));

const SelectStack = styled(Flex)({
  flexDirection: "column",
  gap: 8,
  width: "100%",
});
